/* ============================================================
   ScreenTraceBridge — TASK-165 (issue #271, EPIC-018)
   Owner 2026-09-01: onboarding no longer forces a path, so we need to SEE
   what a new user actually does instead — every screen they land on, how
   long they stay, what they touch first, and where they bail without doing
   anything.

   Three funnel events, all through lib/funnel.js (no-op while its flag is
   off; every call fail-silent):
     screen_view    { screen, prev, dwell_ms }  — on every /app route change
     first_action   { screen, el }              — once per user (trackOnce):
                    the first interactive press after entering the app
     screen_exit_no_action { screen, dwell_ms } — pagehide while the current
                    screen saw zero interactive presses (the "dead end")

   Screen names are normalized route paths (ids replaced with ":id") so the
   admin dashboard can group them; never raw URLs (query stripped — it can
   carry tokens).

   Mounted once inside the /app tree next to OnboardingNudge. Renders null.
   ============================================================ */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { track, trackOnce } from '../lib/funnel.js';
import { normalizeScreen } from '../lib/screenPath.js';

// The normalizer lives in lib/screenPath.js (TASK-196) so funnel.js stamps
// the same screen key on every event's `path`; re-exported for callers
// that import it from here.
export { normalizeScreen };

export default function ScreenTraceBridge() {
  const location = useLocation();
  const currentRef = useRef({ screen: null, enteredAt: Date.now(), acted: false });

  // Screen transitions.
  useEffect(() => {
    const screen = normalizeScreen(location.pathname);
    const prev = currentRef.current;
    if (screen === prev.screen) return;
    const now = Date.now();
    try {
      track('screen_view', {
        screen,
        prev: prev.screen || undefined,
        dwell_ms: prev.screen ? Math.min(now - prev.enteredAt, 30 * 60 * 1000) : undefined,
      });
    } catch { /* fail-silent */ }
    currentRef.current = { screen, enteredAt: now, acted: false };
  }, [location.pathname]);

  // First interactive press: once per user (first_action), and per-screen
  // "acted" flag feeding the dead-end detector below.
  useEffect(() => {
    const onPress = (e) => {
      const el = e.target instanceof Element ? e.target.closest('button, a, input, select, textarea, [role="button"]') : null;
      if (!el) return;
      currentRef.current.acted = true;
      try {
        trackOnce('first_action', {
          screen: currentRef.current.screen || 'dashboard',
          el: (el.tagName || '').toLowerCase(),
        });
      } catch { /* fail-silent */ }
    };
    window.addEventListener('pointerdown', onPress, { passive: true, capture: true });
    return () => window.removeEventListener('pointerdown', onPress, { capture: true });
  }, []);

  // Dead end: the tab goes away while the current screen saw no interaction.
  // funnel.js already flushes its queue on pagehide, so enqueueing here still
  // ships in the same beacon window.
  useEffect(() => {
    const onHide = () => {
      const cur = currentRef.current;
      if (!cur.screen || cur.acted) return;
      try {
        track('screen_exit_no_action', {
          screen: cur.screen,
          dwell_ms: Math.min(Date.now() - cur.enteredAt, 30 * 60 * 1000),
        });
      } catch { /* fail-silent */ }
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  return null;
}
